import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' }
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Helper to get the business unit prefix
const getBuPrefix = () => {
  const bu = localStorage.getItem('businessUnit') || 'mortgage';
  return bu === 'insurance' ? '/insurance' : '';
};

export const auth = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  registerAgent: (data) => api.post('/auth/register-agent', data),
  me: () => api.get('/auth/me'),
  changePassword: (data) => api.put('/auth/change-password', data),
  updateProfile: (data) => api.put('/auth/profile', data),
  getAgentProfile: () => api.get('/auth/agent-profile'),
  updateAgentProfile: (data) => api.put('/auth/agent-profile', data),
  requestPasswordReset: (data) => api.post('/auth/request-password-reset', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
};

export const agents = {
  list: () => api.get(`${getBuPrefix()}/agents`),
  create: (data) => api.post(`${getBuPrefix()}/agents`, data),
  update: (id, data) => api.put(`${getBuPrefix()}/agents/${id}`, data),
  delete: (id) => api.delete(`${getBuPrefix()}/agents/${id}`),
};

export const commissions = {
  list: () => api.get(`${getBuPrefix()}/commissions`),
  create: (data) => api.post(`${getBuPrefix()}/commissions`, data),
  update: (id, data) => api.put(`${getBuPrefix()}/commissions/${id}`, data),
  delete: (id) => api.delete(`${getBuPrefix()}/commissions/${id}`),
};

export const expenses = {
  list: () => api.get(`${getBuPrefix()}/expenses`),
  create: (data) => api.post(`${getBuPrefix()}/expenses`, data),
  update: (id, data) => api.put(`${getBuPrefix()}/expenses/${id}`, data),
  delete: (id) => api.delete(`${getBuPrefix()}/expenses/${id}`),
  approve: (id, status, rejectionReason) => api.put(`${getBuPrefix()}/expenses/${id}/approval`, { status, rejection_reason: rejectionReason }),
  bulkApprove: (ids) => api.post(`${getBuPrefix()}/expenses/bulk-approve`, ids),
  exportCsv: (params) => api.get(`${getBuPrefix()}/expenses/export/csv`, { params, responseType: 'blob' }),
  exportExcel: (params) => api.get(`${getBuPrefix()}/expenses/export/excel`, { params, responseType: 'blob' }),
  pendingApprovals: () => api.get(`${getBuPrefix()}/expenses/pending-approvals`),
};

export const dashboard = {
  stats: (year) => api.get(`${getBuPrefix()}/dashboard/stats${year ? `?year=${year}` : ''}`),
};

export const portal = {
  dashboard: (year) => api.get(`/portal/dashboard${year ? `?year=${year}` : ''}`),
};

export const settings = {
  getCompany: () => {
    const bu = localStorage.getItem('businessUnit') || 'mortgage';
    const endpoint = bu === 'insurance' ? '/settings/insurance/company' : '/settings/company';
    return api.get(endpoint);
  },
  updateCompany: (data) => {
    const bu = localStorage.getItem('businessUnit') || 'mortgage';
    const endpoint = bu === 'insurance' ? '/settings/insurance/company' : '/settings/company';
    return api.put(endpoint, data);
  },
};

export const statements = {
  generate: (data) => api.post(`${getBuPrefix()}/statements/generate`, data),
  downloadPdf: (data) => api.post(`${getBuPrefix()}/statements/pdf`, data, { responseType: 'blob' }),
  email: (data) => api.post(`${getBuPrefix()}/statements/email`, data),
  // Stored statements (by payment date)
  generateByPaymentDate: (data) => api.post(`${getBuPrefix()}/statements/generate-by-payment-date`, data),
  getStored: () => api.get(`${getBuPrefix()}/statements/stored`),
  downloadStoredPdf: (id) => api.get(`${getBuPrefix()}/statements/stored/${id}/pdf`, { responseType: 'blob' }),
  deleteStored: (id) => api.delete(`${getBuPrefix()}/statements/stored/${id}`),
  emailStored: (id) => api.post(`${getBuPrefix()}/statements/stored/${id}/email`),
  getPaymentDates: () => api.get(`${getBuPrefix()}/statements/payment-dates`),
  regenerateAll: () => api.post(`${getBuPrefix()}/statements/regenerate-all`),
};

export const lenders = {
  list: () => {
    const bu = localStorage.getItem('businessUnit') || 'mortgage';
    const endpoint = bu === 'insurance' ? '/insurance/lenders' : '/lenders';
    return api.get(endpoint);
  },
};

export const messages = {
  list: () => api.get('/messages'),
  create: (data) => api.post('/messages', data),
  markRead: (id) => api.post(`/messages/${id}/read`),
  delete: (id) => api.delete(`/messages/${id}`),
};

export const seed = () => api.post('/seed');

export const dataImport = {
  getFields: () => api.get('/import/fields'),
  preview: (file) => {
    const bu = localStorage.getItem('businessUnit') || 'mortgage';
    const endpoint = bu === 'insurance' ? '/insurance/import/preview' : '/import/preview';
    const formData = new FormData();
    formData.append('file', file);
    return api.post(endpoint, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  execute: (sessionId, mappings) => {
    const bu = localStorage.getItem('businessUnit') || 'mortgage';
    const endpoint = bu === 'insurance' ? '/insurance/import/execute' : '/import/execute';
    return api.post(endpoint, { session_id: sessionId, mappings });
  },
  // PDF import methods
  previewPdf: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/import/pdf/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  executePdf: (sessionId, mappings) => {
    return api.post('/import/pdf/execute', { session_id: sessionId, mappings });
  },
  // Batch PDF import
  batchImportPdfs: (files) => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    return api.post('/import/pdf/batch', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

export const importExcel = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`${getBuPrefix()}/import/excel`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export default api;
